import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Eyebrow, Reveal } from "@/components/primitives";
import { APPROACH_STEPS } from "@/data/content";

export default function ApproachTimeline({ showHeader = true }) {
    const ref = useRef(null);
    const { scrollYProgress } = useScroll({
        target: ref,
        offset: ["start 65%", "end 55%"],
    });
    const lineScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

    return (
        <section className="border-t border-border py-24 md:py-32">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                {showHeader && (
                    <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
                        <div className="lg:col-span-8">
                            <Reveal>
                                <Eyebrow>Our Approach</Eyebrow>
                            </Reveal>
                            <Reveal delay={0.05}>
                                <h2 className="mt-5 max-w-3xl text-balance text-4xl font-bold leading-[1.1] tracking-tight md:text-5xl">
                                    A deliberate path from repetitive work to
                                    measurable value
                                </h2>
                            </Reveal>
                        </div>
                        <div className="lg:col-span-4">
                            <Reveal delay={0.1}>
                                <p className="text-lg leading-relaxed text-muted-foreground">
                                    We automate one workflow first, prove the
                                    value, then scale, always inside your
                                    existing systems.
                                </p>
                            </Reveal>
                        </div>
                    </div>
                )}

                <div ref={ref} className="relative mt-16 md:mt-20">
                    {/* rail */}
                    <div className="absolute left-[19px] top-2 h-[calc(100%-1rem)] w-px bg-border md:left-1/2 md:-translate-x-1/2" />
                    <motion.div
                        style={{ scaleY: lineScale }}
                        className="absolute left-[19px] top-2 h-[calc(100%-1rem)] w-px origin-top bg-navy md:left-1/2 md:-translate-x-1/2"
                    />

                    <div className="space-y-10 md:space-y-0">
                        {APPROACH_STEPS.map((s, i) => {
                            const Icon = s.icon;
                            const right = i % 2 === 1;
                            return (
                                <div
                                    key={s.n}
                                    className="relative md:grid md:min-h-[168px] md:grid-cols-2 md:items-center md:gap-16"
                                >
                                    {/* node */}
                                    <div className="absolute left-0 top-1 z-10 md:left-1/2 md:top-1/2 md:-translate-x-1/2 md:-translate-y-1/2">
                                        <motion.div
                                            initial={{ scale: 0, opacity: 0 }}
                                            whileInView={{ scale: 1, opacity: 1 }}
                                            viewport={{ once: true, margin: "-20%" }}
                                            transition={{
                                                duration: 0.5,
                                                ease: [0.16, 1, 0.3, 1],
                                            }}
                                            className="grid h-10 w-10 place-items-center rounded-full border border-border bg-background text-navy"
                                        >
                                            <Icon
                                                className="h-4 w-4"
                                                strokeWidth={1.5}
                                            />
                                        </motion.div>
                                    </div>

                                    <div
                                        className={
                                            right
                                                ? "md:col-start-2 md:pl-4"
                                                : "md:pr-4 md:text-right"
                                        }
                                    >
                                        <Reveal delay={0.05}>
                                            <div className="pl-16 md:pl-0">
                                                <span className="font-mono text-sm text-navy">
                                                    {s.n}
                                                </span>
                                                <h3 className="mt-2 text-2xl font-semibold tracking-tight">
                                                    {s.title}
                                                </h3>
                                                <p className="mt-2 max-w-sm text-[15px] leading-relaxed text-muted-foreground md:inline-block">
                                                    {s.body}
                                                </p>
                                            </div>
                                        </Reveal>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </section>
    );
}
